import { supabase } from '../supabase';
import type { Role, Permission } from '@shiftly/shared';
import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import SupabaseAdminActionService from '../admin-actions/supabaseAdminActionService';

// Convert database record to Role type
const convertDatabaseRecordToRole = (record: any, permissions?: Permission[]): Role => ({
  id: record.id,
  name: record.display_name || record.name,
  permissions: permissions || [],
  level: record.level,
  created_at: DateTime.fromISO(record.created_at, { zone: 'UTC' }),
  updated_at: DateTime.fromISO(record.updated_at, { zone: 'UTC' })
});

// Convert Role type to database record format
const convertRoleToDatabase = (role: Omit<Role, 'id' | 'created_at' | 'updated_at'>) => {
  const now = DateTime.utc();
  return {
    name: role.name.toLowerCase().replace(/\s+/g, '_'), // Create slug from display name
    display_name: role.name,
    level: role.level,
    created_at: now.toISO(),
    updated_at: now.toISO()
  };
};

// Hook to get all roles
export const useRoles = (): UseQueryResult<Role[]> => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('level', { ascending: true });
      
      if (error) throw error;
      return (data || []).map(role => convertDatabaseRecordToRole(role));
    }
  });
};
export const useRolesWithPermissions = (): UseQueryResult<Role[]> => {
  return useQuery({
    queryKey: ['roles_with_permissions'],
    queryFn: async () => {
      // Get roles with their permissions using joins
      const { data, error } = await supabase
        .from('roles')
        .select(`
          *,
          role_permissions!inner(
            permissions(key)
          )
        `)
        .order('level', { ascending: true });

      if (error) {
        if (error.code === 'PGRST116') return [];
        throw error;
      }

      if (!data) return [];

      // Transform the data to include permissions in the correct format
      return data.map(role => {
        const permissions = role.role_permissions?.map((rp: any) => rp.permissions.key as Permission) || [];
        return convertDatabaseRecordToRole(role, permissions);
      });
    },
  });
};

// Hook to get a specific role
export const useRole = (roleId: string): UseQueryResult<Role | null> => {
  return useQuery({
    queryKey: ['roles', roleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roles')
        .select(`
          *,
          role_permissions(
            permissions(key)
          )
        `)
        .eq('id', roleId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      
      if (!data) return null;

      const permissions = data.role_permissions?.map((rp: any) => rp.permissions.key as Permission) || [];
      return convertDatabaseRecordToRole(data, permissions);
    },
    enabled: !!roleId
  });
};

// Hook to create a role
export const useCreateRole = (): UseMutationResult<Role, Error, Omit<Role, 'id' | 'created_at' | 'updated_at'>> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (role: Omit<Role, 'id' | 'created_at' | 'updated_at'>) => {
      // Create the role record
      const roleData = convertRoleToDatabase(role);
      
      const { data: createdRole, error } = await supabase
        .from('roles')
        .insert([roleData])
        .select()
        .single();
      
      if (error) throw error;

      // If permissions are provided, handle permission associations
      if (role.permissions && role.permissions.length > 0) {
        // First, get permission IDs for the provided permission keys
        const { data: permissionData, error: permError } = await supabase
          .from('permissions')
          .select('id, key')
          .in('key', role.permissions);

        if (permError) throw permError;

        if (permissionData && permissionData.length > 0) {
          // Create role-permission associations
          const rolePermissions = permissionData.map(perm => ({
            role_id: createdRole.id,
            permission_id: perm.id
          }));

          const { error: rpError } = await supabase
            .from('role_permissions')
            .insert(rolePermissions);

          if (rpError) throw rpError;
        }
      }
      
      // Get current user for logging
      const { data: { user } } = await supabase.auth.getUser();
      
      // Log admin action
      await SupabaseAdminActionService.logAction({
        type: 'CREATE_ROLE',
        performed_by: user?.id || null,
        target_id: createdRole.id,
        target_type: 'ROLE',
        details: `Created role "${role.name}"`,
        changes: {
          after: convertDatabaseRecordToRole(createdRole, role.permissions)
        }
      });
      
      return convertDatabaseRecordToRole(createdRole, role.permissions);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles_with_permissions'] });
      queryClient.invalidateQueries({ queryKey: ['userPermissions'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
};

// Hook to update a role
export const useUpdateRole = (): UseMutationResult<Role, Error, { id: string; role: Partial<Role> }> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: Partial<Role> }) => {
      // Get the old role for logging with permissions
      const { data: oldRoleData } = await supabase
        .from('roles')
        .select(`
          *,
          role_permissions(
            permissions(key)
          )
        `)
        .eq('id', id)
        .single();

      const oldRole = oldRoleData ? convertDatabaseRecordToRole(
        oldRoleData, 
        oldRoleData.role_permissions?.map((rp: any) => rp.permissions.key as Permission) || []
      ) : null;
      
      // Update role basic info if provided
      const updated_ata: any = {
        updated_at: DateTime.utc().toISO()
      };

      if (role.name !== undefined) {
        updated_ata.display_name = role.name;
        updated_ata.name = role.name.toLowerCase().replace(/\s+/g, '_');
      }
      if (role.level !== undefined) {
        updated_ata.level = role.level;
      }
      
      const { data: updatedRoleData, error } = await supabase
        .from('roles')
        .update(updated_ata)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;

      // Handle permissions update if provided
      if (role.permissions !== undefined) {
        // Delete existing role-permission associations
        await supabase
          .from('role_permissions')
          .delete()
          .eq('role_id', id);

        // Add new permissions if any
        if (role.permissions.length > 0) {
          const { data: permissionData, error: permError } = await supabase
            .from('permissions')
            .select('id, key')
            .in('key', role.permissions);

          if (permError) throw permError;

          if (permissionData && permissionData.length > 0) {
            const rolePermissions = permissionData.map(perm => ({
              role_id: id,
              permission_id: perm.id
            }));

            const { error: rpError } = await supabase
              .from('role_permissions')
              .insert(rolePermissions);

            if (rpError) throw rpError;
          }
        }
      }
      
      // Get current user for logging
      const { data: { user } } = await supabase.auth.getUser();
      
      const updatedRole = convertDatabaseRecordToRole(updatedRoleData, role.permissions || oldRole?.permissions);
      
      // Log admin action
      await SupabaseAdminActionService.logAction({
        type: 'EDIT_ROLE',
        performed_by: user?.id || null,
        target_id: id,
        target_type: 'ROLE',
        details: `Updated role "${oldRole?.name || updatedRole.name}"`,
        changes: {
          before: oldRole,
          after: updatedRole
        }
      });
      
      return updatedRole;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles_with_permissions'] });
      queryClient.invalidateQueries({ queryKey: ['userPermissions'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
};

// Hook to delete a role
export const useDeleteRole = (): UseMutationResult<string, Error, string> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (roleId: string) => {
      // Get the old role for logging with permissions
      const { data: oldRoleData } = await supabase
        .from('roles')
        .select(`
          *,
          role_permissions(
            permissions(key)
          )
        `)
        .eq('id', roleId)
        .single();

      const oldRole = oldRoleData ? convertDatabaseRecordToRole(
        oldRoleData,
        oldRoleData.role_permissions?.map((rp: any) => rp.permissions.key as Permission) || []
      ) : null;
      
      // Delete role (CASCADE will handle role_permissions and user_roles)
      const { error } = await supabase
        .from('roles')
        .delete()
        .eq('id', roleId);
      
      if (error) throw error;
      
      // Get current user for logging
      const { data: { user } } = await supabase.auth.getUser();
      
      // Log admin action
      await SupabaseAdminActionService.logAction({
        type: 'DELETE_ROLE',
        performed_by: user?.id || null,
        target_id: roleId,
        target_type: 'ROLE',
        details: `Deleted role "${oldRole?.name || 'Unknown'}"`,
        changes: {
          before: oldRole
        }
      });
      
      return roleId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles_with_permissions'] });
      queryClient.invalidateQueries({ queryKey: ['userPermissions'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
};

// Hook to check if a user has a specific permission
export const useHasPermission = (user_id: string, permission: Permission): UseQueryResult<boolean> => {
  return useQuery({
    queryKey: ['userPermissions', user_id, permission],
    queryFn: async () => {
      if (!user_id || !permission) return false;

      // Get user's permissions via roles using a single query with joins
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          role_permissions!inner(
            permissions!inner(key)
          )
        `)
        .eq('user_id', user_id);

      if (error || !data) return false;

      // Check if the requested permission exists in the user's permissions
      return data.some(ur => 
        ur.role_permissions.some((rp: any) => 
          rp.permissions.key === permission
        )
      );
    },
    enabled: !!user_id && !!permission,
  });
};

// Hook to get all permissions for a user
export const useUserPermissions = (user_id: string): UseQueryResult<Set<Permission>> => {
  return useQuery({
    queryKey: ['userPermissions', user_id],
    queryFn: async () => {
      if (!user_id) return new Set<Permission>();

      // Get all permissions for the user via roles using a single query with joins
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          role_permissions(
            permissions(key)
          )
        `)
        .eq('user_id', user_id);

      if (error || !data) return new Set<Permission>();

      // Combine all permissions into a Set to remove duplicates
      const permissions = new Set<Permission>();
      
      data.forEach(ur => {
        ur.role_permissions?.forEach((rp: any) => {
          if (rp.permissions?.key) {
            permissions.add(rp.permissions.key as Permission);
          }
        });
      });

      return permissions;
    },
    enabled: !!user_id,
  });
};